import { useEffect, useState } from "preact/hooks";
import type { RoutableProps } from "preact-router";
import { appInfo, parseHashIndicator, type HashIndicator } from "@sage/shared";

import { Navbar } from "../../components/layout/Navbar";
import { UploadZone } from "../../components/upload/UploadZone";
import { ReportCard } from "../../components/reputation/ReportCard";
import { VerdictBadge } from "../../components/reputation/VerdictBadge";
import { SearchOptions, type SearchOption } from "../search/SearchOptions";
import { getClerkAuthToken } from "../../auth/clerk";
import { lookupReputation } from "../../lib/reputation-client";
import { mapLookupFailure, type LookupStatus, type ReportVisibility } from "../../lib/lookup-status";

export function HomePage(_props: RoutableProps) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchOption, setSearchOption] = useState<SearchOption>({
    kind: "hash",
  });
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState<LookupStatus>({ kind: "idle" });

  useEffect(() => {
    document.title = `${appInfo.name} | Home`;
  }, []);

  async function handleSearchSubmit(event?: Event) {
    event?.preventDefault();

    if (searchOption.kind !== "hash") {
      setStatus({
        kind: "not_supported",
        message: `${searchOption.kind === "name" ? "Name" : "Fuzzy hash"} search is not available yet. Use hash lookup for now.`,
      });
      return;
    }

    if (searchValue.length === 0) {
      setStatus({ kind: "invalid", message: "Enter a raw MD5, SHA1, or SHA256 hash." });
      return;
    }

    let hash: HashIndicator;
    try {
      hash = parseHashIndicator(searchValue);
    } catch {
      setStatus({
        kind: "invalid",
        message: "Use only the raw hex hash: 32, 40, or 64 characters with no prefixes, paths, or spaces.",
      });
      return;
    }

    setStatus({ kind: "loading", hash });

    const token = await getClerkAuthToken();
    const result = await lookupReputation({ hash: hash.value, token });

    if (!result.ok) {
      setStatus(mapLookupFailure(result.error));
      return;
    }

    const visibility: ReportVisibility = token ? "private" : "public";
    setStatus(
      result.report.verdict === "unknown"
        ? { kind: "unknown", report: result.report, visibility }
        : { kind: "success", report: result.report, visibility }
    );
  }

  const placeholder =
    searchOption.kind === "hash"
      ? "Paste MD5, SHA1, or SHA256 hash"
      : searchOption.kind === "name"
        ? "Enter file or malware name"
        : "Paste SSDEEP or TLSH hash";

  const hint =
    searchOption.kind === "hash"
      ? "e.g., e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"
      : searchOption.kind === "name"
        ? "e.g., trojan.agent"
        : "e.g., 3:AXGB:AXGB or T1B8C4...";

  return (
    <div class="app-shell">
      <Navbar />

      <main class="home-main">
        <div class="hero-card">
          <div class="hero-card__buttons">
            <button
              class={`hero-card__btn${selectedTab === 0 ? " hero-card__btn--active" : ""}`}
              onClick={() => setSelectedTab(0)}
            >
              Upload
            </button>
            <button
              class={`hero-card__btn${selectedTab === 1 ? " hero-card__btn--active" : ""}`}
              onClick={() => setSelectedTab(1)}
            >
              Search
            </button>
          </div>

          {selectedTab === 0 ? (
            <div class="home-tabs__panel">
              <UploadZone />
            </div>
          ) : (
            <div class="home-tabs__panel">
              <SearchOptions value={searchOption} onChange={setSearchOption} />

              <form class="home-search-form" onSubmit={handleSearchSubmit} noValidate>
                <div class="home-search-row">
                  <input
                    class="home-search-input"
                    type="text"
                    inputMode="text"
                    spellcheck={false}
                    autocomplete="off"
                    value={searchValue}
                    placeholder={placeholder}
                    aria-label="Search value"
                    aria-invalid={status.kind === "invalid"}
                    onInput={(event) => setSearchValue(event.currentTarget.value)}
                  />
                  <button
                    class="home-search-button"
                    type="submit"
                    disabled={status.kind === "loading"}
                  >
                    {status.kind === "loading" ? "Searching..." : "Search"}
                  </button>
                </div>

                <p class="home-search-hint">{hint}</p>

                <div class="home-search-status" role="status" aria-live="polite">
                  {renderSearchStatus(status)}
                </div>
              </form>

              {(status.kind === "success" || status.kind === "unknown") ? (
                <div class="home-search-result">
                  <VerdictBadge verdict={status.report.verdict} />
                  <span class="home-search-result__hash">{status.report.hash.value}</span>
                  <span class="home-search-result__confidence">
                    Confidence: {status.report.confidence}% · Detections: {status.report.detections}
                  </span>
                  <span class="home-search-result__visibility">
                    {status.visibility === "private" ? "Private workspace" : "Public/community"}
                  </span>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function renderSearchStatus(status: LookupStatus) {
  switch (status.kind) {
    case "idle":
      return null;
    case "loading":
      return `Looking up ${status.hash.algorithm.toUpperCase()} ${status.hash.value}...`;
    case "success":
      return "Lookup complete.";
    case "unknown":
      return "Lookup complete. No malicious reputation is known yet.";
    default:
      return <span class="home-search-status--error">{status.message}</span>;
  }
}
